import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Edit, FileText, FileDown, Search, X, ShoppingCart, Send, PackageCheck, Ban, Clock, ClipboardList, Undo2, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { WhatsAppShareButton } from "@/components/WhatsAppShare";

const ESTADO_LABEL = {
  borrador: "Borrador",
  enviada: "Enviada",
  recibida: "Recibida",
  cancelada: "Cancelada",
  revertida: "Revertida",
};

const ESTADO_BADGE = {
  borrador: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", Icon: Clock },
  enviada: { bg: "bg-blue-50", text: "text-[rgb(0,47,167)]", border: "border-blue-200", Icon: Send },
  recibida: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", Icon: PackageCheck },
  cancelada: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", Icon: Ban },
  revertida: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", Icon: Undo2 },
};

const emptyForm = () => ({
  supplier_id: "",
  supplier_name: "",
  fecha: new Date().toISOString().split("T")[0],
  fecha_entrega: "",
  estado: "borrador",
  lineas: [],
  observaciones: "",
});

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Recibir (Receive PO → entrada de inventario) state
  const [recibirPo, setRecibirPo] = useState(null);
  const [recibirLines, setRecibirLines] = useState([]);
  const [recibirNota, setRecibirNota] = useState("");
  const [recibirLoading, setRecibirLoading] = useState(false);

  // Revertir (reverse inventory entry) state
  const [revertirPo, setRevertirPo] = useState(null);
  const [revertirPassword, setRevertirPassword] = useState("");
  const [revertirMotivo, setRevertirMotivo] = useState("");
  const [revertirAck, setRevertirAck] = useState(false);
  const [revertirLoading, setRevertirLoading] = useState(false);

  const openRevertir = (po) => {
    setRevertirPo(po);
    setRevertirPassword("");
    setRevertirMotivo("");
    setRevertirAck(false);
  };

  const closeRevertir = () => {
    setRevertirPo(null);
    setRevertirPassword("");
    setRevertirMotivo("");
    setRevertirAck(false);
  };

  const submitRevertir = async () => {
    if (!revertirPo) return;
    if (!revertirAck) {
      toast.error("Debes confirmar que entendiste que esta acción no se puede deshacer");
      return;
    }
    if (!revertirPassword) {
      toast.error("Ingresa tu contraseña para confirmar");
      return;
    }
    setRevertirLoading(true);
    try {
      await api.post(`/purchase-orders/${revertirPo.id}/revertir`, {
        password: revertirPassword,
        motivo: revertirMotivo,
      });
      toast.success(`Recepción revertida — inventario restaurado para ${revertirPo.numero}`);
      closeRevertir();
      fetchAll();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error("Contraseña incorrecta");
      } else {
        toast.error(err.response?.data?.detail || "Error al revertir");
      }
    } finally {
      setRevertirLoading(false);
    }
  };

  const openRecibir = (po) => {
    setRecibirPo(po);
    setRecibirLines((po.lineas || []).map(l => ({
      codigo: l.codigo,
      descripcion: l.descripcion || l.codigo,
      cantidad_solicitada: l.cantidad,
      cantidad: l.cantidad,           // by default receive all
      costo_unitario: l.costo_nacionalizado || l.costo_unitario || 0,
    })));
    setRecibirNota("");
  };

  const submitRecibir = async () => {
    if (!recibirPo) return;
    const lineas = recibirLines
      .filter(l => parseFloat(l.cantidad) > 0)
      .map(l => ({
        codigo: l.codigo,
        cantidad: parseFloat(l.cantidad) || 0,
        costo_unitario: parseFloat(l.costo_unitario) || 0,
      }));
    if (lineas.length === 0) {
      toast.error("Debe haber al menos una línea con cantidad > 0");
      return;
    }
    setRecibirLoading(true);
    try {
      await api.post(`/purchase-orders/${recibirPo.id}/recibir`, { lineas, nota: recibirNota });
      toast.success(`Mercadería ingresada al inventario`);
      setRecibirPo(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al recibir");
    } finally {
      setRecibirLoading(false);
    }
  };
  const [formData, setFormData] = useState(emptyForm());
  const [productSearch, setProductSearch] = useState("");
  const [onlyFromSelectedSupplier, setOnlyFromSelectedSupplier] = useState(true);
  const [statusFilter, setStatusFilter] = useState("__all__");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [poRes, supRes, prodRes] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/suppliers"),
        api.get("/products"),
      ]);
      setPos(poRes.data);
      setSuppliers(supRes.data);
      setProducts(prodRes.data);
    } catch {
      toast.error("Error al cargar órdenes de compra");
    } finally {
      setLoading(false);
    }
  };

  const supplierMap = useMemo(() => Object.fromEntries(suppliers.map(s => [s.id, s])), [suppliers]);

  const filteredPos = useMemo(() => {
    if (statusFilter === "__all__") return pos;
    return pos.filter(p => p.estado === statusFilter);
  }, [pos, statusFilter]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    // 1) Optional pre-filter by selected supplier (matches by name, case-insensitive).
    let pool = products;
    const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);
    if (onlyFromSelectedSupplier && selectedSupplier) {
      const supName = (selectedSupplier.nombre || "").trim().toLowerCase();
      if (supName) {
        pool = products.filter(p => (p.proveedor || "").trim().toLowerCase() === supName);
      }
    }
    if (!q) return pool.slice(0, 30);
    return pool.filter(p =>
      (p.codigo_interno || "").toLowerCase().includes(q) ||
      (p.codigo || "").toLowerCase().includes(q) ||
      (p.descripcion || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [products, productSearch, suppliers, formData.supplier_id, onlyFromSelectedSupplier]);

  const supplierProductCount = useMemo(() => {
    const sel = suppliers.find(s => s.id === formData.supplier_id);
    if (!sel) return 0;
    const supName = (sel.nombre || "").trim().toLowerCase();
    if (!supName) return 0;
    return products.filter(p => (p.proveedor || "").trim().toLowerCase() === supName).length;
  }, [suppliers, formData.supplier_id, products]);

  const cartSubtotal = useMemo(
    () => formData.lineas.reduce((s, l) => s + (parseFloat(l.subtotal) || 0), 0),
    [formData.lineas]
  );

  const openNew = () => {
    setEditingPo(null);
    setFormData(emptyForm());
    setProductSearch("");
    setIsDialogOpen(true);
  };

  const openEdit = (po) => {
    setEditingPo(po);
    setFormData({
      supplier_id: po.supplier_id || "",
      supplier_name: po.supplier_name || "",
      fecha: po.fecha || "",
      fecha_entrega: po.fecha_entrega || "",
      estado: po.estado || "borrador",
      lineas: (po.lineas || []).map(l => ({ ...l })),
      observaciones: po.observaciones || "",
    });
    setProductSearch("");
    setIsDialogOpen(true);
  };

  const addToCart = (product) => {
    const existingIdx = formData.lineas.findIndex(l =>
      (l.codigo_interno && l.codigo_interno === product.codigo_interno) ||
      l.codigo === product.codigo
    );
    const unitCost = product.costo_nacionalizado > 0 ? product.costo_nacionalizado : (product.costo || 0);
    if (existingIdx >= 0) {
      const next = [...formData.lineas];
      next[existingIdx] = {
        ...next[existingIdx],
        cantidad: (parseInt(next[existingIdx].cantidad, 10) || 0) + 1,
      };
      next[existingIdx].subtotal = (next[existingIdx].cantidad || 0) * (parseFloat(next[existingIdx].costo_nacionalizado) > 0
        ? parseFloat(next[existingIdx].costo_nacionalizado)
        : parseFloat(next[existingIdx].costo_unitario) || 0);
      setFormData({ ...formData, lineas: next });
    } else {
      setFormData({
        ...formData,
        lineas: [
          ...formData.lineas,
          {
            codigo: product.codigo || "",
            codigo_interno: product.codigo_interno || "",
            descripcion: product.descripcion || "",
            cantidad: 1,
            costo_unitario: product.costo || 0,
            costo_nacionalizado: product.costo_nacionalizado || 0,
            subtotal: unitCost,
          },
        ],
      });
    }
  };

  const updateLine = (idx, patch) => {
    const next = [...formData.lineas];
    const cur = { ...next[idx], ...patch };
    const cantidad = parseInt(cur.cantidad, 10) || 0;
    const cn = parseFloat(cur.costo_nacionalizado) || 0;
    const cu = parseFloat(cur.costo_unitario) || 0;
    const unit = cn > 0 ? cn : cu;
    cur.subtotal = Math.round(cantidad * unit * 100) / 100;
    next[idx] = cur;
    setFormData({ ...formData, lineas: next });
  };

  const removeLine = (idx) => {
    setFormData({ ...formData, lineas: formData.lineas.filter((_, i) => i !== idx) });
  };

  const handleSupplierChange = (supplierId) => {
    const sup = supplierMap[supplierId];
    setFormData({ ...formData, supplier_id: supplierId, supplier_name: sup?.nombre || "" });
  };

  const submit = async () => {
    if (!formData.supplier_id) {
      toast.error("Selecciona un proveedor");
      return;
    }
    if (formData.lineas.length === 0) {
      toast.error("Agrega al menos un producto al carrito");
      return;
    }
    try {
      const payload = {
        ...formData,
        lineas: formData.lineas.map(l => ({
          ...l,
          cantidad: parseInt(l.cantidad, 10) || 0,
          costo_unitario: parseFloat(l.costo_unitario) || 0,
          costo_nacionalizado: parseFloat(l.costo_nacionalizado) || 0,
          subtotal: parseFloat(l.subtotal) || 0,
        })),
      };
      if (editingPo) {
        await api.put(`/purchase-orders/${editingPo.id}`, { ...payload, id: editingPo.id });
        toast.success("Orden de compra actualizada");
      } else {
        await api.post("/purchase-orders", payload);
        toast.success("Orden de compra creada");
      }
      setIsDialogOpen(false);
      setEditingPo(null);
      setFormData(emptyForm());
      fetchAll();
    } catch {
      toast.error("Error al guardar orden de compra");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/purchase-orders/${deleteTarget.id}`);
      toast.success("Orden de compra eliminada");
      setDeleteTarget(null);
      fetchAll();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await api.post("/purchase-orders/bulk-delete", selectedIds);
      toast.success(`${selectedIds.length} órdenes eliminadas`);
      setSelectedIds([]);
      fetchAll();
    } catch {
      toast.error("Error al eliminar en lote");
    }
  };

  const exportPdf = async (po, lang) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-orders/${po.id}/export?lang=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${po.numero || "OC"}_${lang}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF (${lang.toUpperCase()}) descargado`);
    } catch {
      toast.error("Error al exportar PDF");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (loading) return <PageLoader label="Cargando órdenes de compra…" testid="po-loader" />;

  return (
    <div data-testid="purchase-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Órdenes de Compra
          </h2>
          <p className="text-slate-500 text-sm mt-1">Gestiona las órdenes a tus proveedores</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button onClick={bulkDelete} variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-po-button">
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar ({selectedIds.length})
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="btn-primary rounded-sm" data-testid="new-po-button">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Orden
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPo ? `Editar Orden ${editingPo.numero}` : "Nueva Orden de Compra"}</DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-12 gap-5 mt-3">
                {/* LEFT: Header + Product search */}
                <div className="col-span-7 space-y-4">
                  <Card className="bg-white border border-slate-200 rounded-sm p-4">
                    <h3 className="text-sm font-semibold mb-3">Datos generales</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-medium mb-1 text-slate-600">Proveedor *</label>
                        <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                          <SelectTrigger className="rounded-sm" data-testid="po-supplier-select">
                            <SelectValue placeholder="Selecciona un proveedor" />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-slate-500">No hay proveedores registrados</div>
                            ) : suppliers.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-slate-600">Fecha</label>
                        <Input type="date" value={formData.fecha} onChange={e => setFormData({ ...formData, fecha: e.target.value })} className="rounded-sm" data-testid="po-date-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-slate-600">Entrega estimada (ETA)</label>
                        <Input type="date" value={formData.fecha_entrega} onChange={e => setFormData({ ...formData, fecha_entrega: e.target.value })} className="rounded-sm" data-testid="po-eta-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-slate-600">Estado</label>
                        <Select value={formData.estado} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
                          <SelectTrigger className="rounded-sm" data-testid="po-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="borrador">Borrador</SelectItem>
                            <SelectItem value="enviada">Enviada</SelectItem>
                            <SelectItem value="recibida">Recibida</SelectItem>
                            <SelectItem value="cancelada">Cancelada</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-slate-600">Observaciones</label>
                        <Input value={formData.observaciones} onChange={e => setFormData({ ...formData, observaciones: e.target.value })} className="rounded-sm" placeholder="Notas internas..." data-testid="po-notes-input" />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-white border border-slate-200 rounded-sm p-4">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div>
                        <h3 className="text-sm font-semibold">Buscar productos</h3>
                        <p className="text-xs text-slate-500">Busca por código interno o descripción. Click para agregar al carrito.</p>
                      </div>
                      {formData.supplier_id && (
                        <label
                          className={`flex items-center gap-2 cursor-pointer px-2 py-1 rounded-sm border text-xs whitespace-nowrap transition-colors ${
                            onlyFromSelectedSupplier
                              ? "bg-blue-50 border-[rgb(0,47,167)] text-[rgb(0,47,167)]"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                          data-testid="po-filter-by-supplier-toggle"
                        >
                          <input
                            type="checkbox"
                            checked={onlyFromSelectedSupplier}
                            onChange={(e) => setOnlyFromSelectedSupplier(e.target.checked)}
                            className="accent-[rgb(0,47,167)] h-3.5 w-3.5"
                          />
                          <span className="font-medium">Solo de este proveedor</span>
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">
                            {supplierProductCount}
                          </span>
                        </label>
                      )}
                    </div>
                    {formData.supplier_id && onlyFromSelectedSupplier && supplierProductCount === 0 && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2 mb-2" data-testid="po-no-products-for-supplier">
                        Este proveedor no tiene productos asignados. Editá los productos en /products y asigná este proveedor en el campo "Proveedor", o desactivá el filtro arriba.
                      </p>
                    )}
                    <div className="relative mb-3 mt-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                        className="pl-9 rounded-sm"
                        placeholder="Código interno o descripción..."
                        data-testid="po-product-search"
                      />
                    </div>
                    <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-72 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="p-4 text-xs text-slate-500 text-center">Sin resultados</div>
                      ) : filteredProducts.map(p => {
                        const unit = p.costo_nacionalizado > 0 ? p.costo_nacionalizado : (p.costo || 0);
                        return (
                          <button
                            type="button"
                            key={p.id}
                            onClick={() => addToCart(p)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                            data-testid={`add-product-${p.id}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{p.descripcion}</div>
                              <div className="text-[11px] text-slate-500 mono">
                                {p.codigo_interno ? `INT: ${p.codigo_interno} · ` : ""}{p.codigo}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-xs font-bold text-[rgb(0,47,167)]">${unit.toFixed(2)}</div>
                              <Plus className="h-3.5 w-3.5 text-slate-400 inline" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Card>
                </div>

                {/* RIGHT: Cart */}
                <div className="col-span-5">
                  <Card className="bg-white border border-slate-200 rounded-sm p-4 sticky top-2">
                    <div className="flex items-center gap-2 mb-3">
                      <ShoppingCart className="h-5 w-5 text-[rgb(0,47,167)]" />
                      <h3 className="text-sm font-semibold">Carrito</h3>
                      <span className="text-xs text-slate-500">{formData.lineas.length} items</span>
                    </div>

                    {formData.lineas.length === 0 ? (
                      <div className="text-xs text-slate-500 text-center py-10 border border-dashed border-slate-200 rounded-sm">
                        El carrito está vacío. Buscá y agregá productos.
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                        {formData.lineas.map((line, idx) => (
                          <div key={`${line.codigo || "row"}-${idx}`} className="border border-slate-200 rounded-sm p-2.5" data-testid={`cart-line-${idx}`}>
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium truncate">{line.descripcion}</div>
                                <div className="text-[10px] text-slate-500 mono">
                                  {line.codigo_interno ? `INT: ${line.codigo_interno} · ` : ""}{line.codigo}
                                </div>
                              </div>
                              <Button type="button" onClick={() => removeLine(idx)} variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:bg-red-50 flex-shrink-0">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-1.5">
                              <div>
                                <label className="block text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Cant.</label>
                                <Input
                                  type="number"
                                  step="1"
                                  min="1"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={line.cantidad}
                                  onKeyDown={(e) => {
                                    // Block ".", "," and "e"/"-" so only positive integers can be typed.
                                    if ([".", ",", "e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                                  }}
                                  onChange={e => {
                                    const v = e.target.value.replace(/[^0-9]/g, "");
                                    updateLine(idx, { cantidad: v === "" ? "" : parseInt(v, 10) });
                                  }}
                                  className="h-7 text-xs rounded-sm"
                                  data-testid={`cart-qty-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="block text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Costo unit.</label>
                                <Input type="number" step="0.01" min="0" value={line.costo_nacionalizado > 0 ? line.costo_nacionalizado : line.costo_unitario} onChange={e => updateLine(idx, line.costo_nacionalizado > 0 ? { costo_nacionalizado: e.target.value } : { costo_unitario: e.target.value })} className="h-7 text-xs rounded-sm" data-testid={`cart-cost-${idx}`} />
                              </div>
                              <div className="text-right">
                                <label className="block text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Subtotal</label>
                                <div className="text-xs font-bold pt-1.5">{formatMoney(line.subtotal || 0)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-slate-200">
                      <div className="flex justify-between items-center text-base">
                        <span className="font-semibold">Total</span>
                        <span className="font-bold text-[rgb(0,47,167)]" data-testid="cart-total">{formatMoney(cartSubtotal)}</span>
                      </div>
                    </div>

                    <Button onClick={submit} className="btn-primary rounded-sm w-full mt-4" data-testid="save-po-button">
                      {editingPo ? "Actualizar Orden" : "Crear Orden"}
                    </Button>
                  </Card>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Filtrar por estado:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-44" data-testid="po-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              <SelectItem value="borrador">Borrador</SelectItem>
              <SelectItem value="enviada">Enviada</SelectItem>
              <SelectItem value="recibida">Recibida</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 ml-auto">{filteredPos.length} {filteredPos.length === 1 ? "orden" : "órdenes"}</span>
        </div>
      </Card>

      {/* Table or empty state */}
      {filteredPos.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={pos.length === 0 ? "Aún no hay órdenes de compra" : "Sin coincidencias"}
          description={
            pos.length === 0
              ? "Generá órdenes de compra a tus proveedores, hacé seguimiento del estado y exportá PDFs en español/inglés."
              : "No encontramos órdenes con esos filtros. Ajustá la búsqueda o el estado."
          }
          actionLabel={pos.length === 0 ? "Nueva orden" : undefined}
          actionOnClick={pos.length === 0 ? openNew : undefined}
          testid="po-empty-state"
        />
      ) : (
      <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-3 w-10">
                  <Checkbox
                    checked={filteredPos.length > 0 && selectedIds.length === filteredPos.length}
                    onCheckedChange={(checked) =>
                      setSelectedIds(checked ? filteredPos.map(p => p.id) : [])
                    }
                  />
                </th>
                <th className="text-left py-3 px-3 font-medium text-slate-600 mono">N°</th>
                <th className="text-left py-3 px-3 font-medium text-slate-600">Fecha</th>
                <th className="text-left py-3 px-3 font-medium text-slate-600">Proveedor</th>
                <th className="text-left py-3 px-3 font-medium text-slate-600">ETA</th>
                <th className="text-left py-3 px-3 font-medium text-slate-600">Estado</th>
                <th className="text-right py-3 px-3 font-medium text-slate-600">Total</th>
                <th className="text-right py-3 px-3 font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredPos.map(po => {
                const badge = ESTADO_BADGE[po.estado] || ESTADO_BADGE.borrador;
                const BadgeIcon = badge.Icon;
                return (
                  <tr key={po.id} className="table-row border-b border-slate-100 cursor-pointer" data-testid="po-row" onClick={() => openEdit(po)}>
                    <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.includes(po.id)} onCheckedChange={() => toggleSelect(po.id)} />
                    </td>
                    <td className="py-3 px-3 mono font-medium">{po.numero}</td>
                    <td className="py-3 px-3 text-slate-600">{po.fecha}</td>
                    <td className="py-3 px-3">{po.supplier_name || "-"}</td>
                    <td className="py-3 px-3 text-slate-600">{po.fecha_entrega || "-"}</td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm border ${badge.bg} ${badge.text} ${badge.border}`}>
                        <BadgeIcon className="h-3 w-3" />
                        {ESTADO_LABEL[po.estado]}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right font-bold text-[rgb(0,47,167)]">{formatMoney(po.total || 0)}</td>
                    <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        {po.estado !== "recibida" && po.estado !== "cancelada" && po.estado !== "revertida" && (
                          <Button onClick={() => openRecibir(po)} size="sm" className="h-8 px-2 text-xs rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`receive-po-${po.id}`} title="Recibir mercadería (entra a inventario)">
                            <PackageCheck className="h-3.5 w-3.5 mr-1" />Recibir
                          </Button>
                        )}
                        <Button onClick={() => exportPdf(po, "es")} variant="ghost" size="sm" className="h-8 px-2 text-xs rounded-sm" title="Exportar PDF (Español)" data-testid={`export-es-${po.id}`}>
                          <FileText className="h-3.5 w-3.5 mr-1" />ES
                        </Button>
                        <Button onClick={() => exportPdf(po, "en")} variant="ghost" size="sm" className="h-8 px-2 text-xs rounded-sm" title="Export PDF (English)" data-testid={`export-en-${po.id}`}>
                          <FileDown className="h-3.5 w-3.5 mr-1" />EN
                        </Button>
                        <WhatsAppShareButton type="purchase_order" id={po.id} label="WA" size="sm" testid={`po-wa-${po.id}`} />
                        {po.estado !== "recibida" && po.estado !== "revertida" && (
                          <Button onClick={() => openEdit(po)} variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm">
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {po.estado === "recibida" ? (
                          <Button
                            onClick={() => openRevertir(po)}
                            variant="ghost" size="sm"
                            className="h-8 px-2 text-xs rounded-sm text-amber-700 hover:bg-amber-50 border border-amber-200"
                            title="Revertir recepción (saca del inventario)"
                            data-testid={`revert-po-${po.id}`}
                          >
                            <Undo2 className="h-3.5 w-3.5 mr-1" />Revertir
                          </Button>
                        ) : po.estado !== "revertida" && (
                          <Button onClick={() => setDeleteTarget(po)} variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm text-red-600 hover:bg-red-50" data-testid={`delete-po-${po.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar orden de compra?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar la orden {deleteTarget?.numero}. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recibir PO dialog — admin edits actual received quantities/costs.
         Each kept line writes a positive StockMovement (entrada) tagged with
         the PO numero, surfacing in the product Kardex. */}
      <Dialog open={!!recibirPo} onOpenChange={(o) => !o && setRecibirPo(null)}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-emerald-600" />
              Recibir orden {recibirPo?.numero}
            </DialogTitle>
          </DialogHeader>
          {recibirPo && (
            <div className="mt-2 space-y-3">
              <p className="text-xs text-slate-500">
                Edita las cantidades realmente recibidas y el costo unitario. Al confirmar, cada línea suma al inventario y queda en el Kardex.
              </p>
              <div className="overflow-x-auto max-h-72 border border-slate-200 rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-2 font-medium">Código</th>
                      <th className="text-left py-2 px-2 font-medium">Producto</th>
                      <th className="text-right py-2 px-2 font-medium w-20">Solicit.</th>
                      <th className="text-right py-2 px-2 font-medium w-24">Recib.</th>
                      <th className="text-right py-2 px-2 font-medium w-32">Costo unit.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recibirLines.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1.5 px-2 mono">{l.codigo}</td>
                        <td className="py-1.5 px-2">{l.descripcion}</td>
                        <td className="py-1.5 px-2 text-right text-slate-500 mono">{l.cantidad_solicitada}</td>
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" min="0" step="any" value={l.cantidad}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRecibirLines(prev => prev.map((x, k) => k === i ? { ...x, cantidad: v } : x));
                            }}
                            className="h-7 rounded-sm text-right mono w-20"
                            data-testid={`recibir-line-qty-${i}`} />
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" min="0" step="any" value={l.costo_unitario}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRecibirLines(prev => prev.map((x, k) => k === i ? { ...x, costo_unitario: v } : x));
                            }}
                            className="h-7 rounded-sm text-right mono w-24"
                            data-testid={`recibir-line-cost-${i}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Nota (opcional)</label>
                <Input value={recibirNota} onChange={(e) => setRecibirNota(e.target.value)} className="rounded-sm" placeholder="Ej: Faltó 1 caja, llegó con golpe…" data-testid="recibir-nota-input" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setRecibirPo(null)} className="rounded-sm" disabled={recibirLoading}>Cancelar</Button>
                <Button type="button" onClick={submitRecibir} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" disabled={recibirLoading} data-testid="recibir-submit-button">
                  <PackageCheck className="h-4 w-4 mr-2" />{recibirLoading ? "Recibiendo…" : "Confirmar recepción"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revertir recepción — pide contraseña + confirmación explícita.
          Backend: POST /api/purchase-orders/{id}/revertir { password, motivo }.
          Solo aplica cuando estado === 'recibida'. */}
      <Dialog open={!!revertirPo} onOpenChange={(o) => !o && closeRevertir()}>
        <DialogContent className="max-w-md rounded-sm" data-testid="revert-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Revertir recepción {revertirPo?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="revert-warning">
              <p className="font-semibold mb-1">Esta acción es irreversible</p>
              <p>
                Se generará una salida de inventario que anula la entrada original.
                Los movimientos originales se conservan en el Kardex para auditoría,
                pero la orden quedará en estado <span className="font-mono">"revertida"</span>
                y no podrá volver a recibirse.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Motivo (opcional)</label>
              <Input
                value={revertirMotivo}
                onChange={(e) => setRevertirMotivo(e.target.value)}
                placeholder="Ej: Mercadería defectuosa, error de captura…"
                className="rounded-sm"
                data-testid="revert-motivo-input"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                <Lock className="h-3 w-3" /> Contraseña actual
              </label>
              <Input
                type="password"
                value={revertirPassword}
                onChange={(e) => setRevertirPassword(e.target.value)}
                placeholder="Ingresá tu contraseña para confirmar"
                className="rounded-sm"
                autoComplete="current-password"
                data-testid="revert-password-input"
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="revert-ack-row">
              <Checkbox
                checked={revertirAck}
                onCheckedChange={(v) => setRevertirAck(!!v)}
                data-testid="revert-ack-checkbox"
              />
              <span>
                Confirmo que entiendo que <strong>esta acción no se puede deshacer</strong>
                y se ajustará el inventario inmediatamente.
              </span>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeRevertir} className="rounded-sm" disabled={revertirLoading} data-testid="revert-cancel-button">
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={submitRevertir}
                className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
                disabled={revertirLoading || !revertirAck || !revertirPassword}
                data-testid="revert-confirm-button"
              >
                <Undo2 className="h-4 w-4 mr-2" />
                {revertirLoading ? "Revirtiendo…" : "Revertir recepción"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
